import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Check, Loader2, ShieldCheck } from "lucide-react";
import { API } from "@/portal/AuthContext";
import { NEON } from "@/components/Campaign";

const subscribe = async ({ email, name, hp, cc, ca }) => {
  const r = await fetch(`${API}/newsletter/house/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: email.trim(), name: (name || "").trim(), hp, cc, ca }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(d?.detail?.message || "Das hat nicht geklappt. Bitte prüfe deine Eingaben.");
    e.code = d?.detail?.code;
    throw e;
  }
  return d.status || "pending";
};

/* Kleine Rechen-Aufgabe (Bot-Schutz) – holt sich eine signierte Challenge vom Server. */
const useChallenge = () => {
  const [chal, setChal] = useState(null);
  const [ans, setAns] = useState("");
  const reload = useCallback(async () => {
    setAns("");
    try {
      const r = await fetch(`${API}/newsletter/challenge`);
      setChal(await r.json());
    } catch {
      setChal(null);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { chal, ans, setAns, reload };
};

const Honeypot = ({ value, onChange, testId }) => (
  <input
    type="text" name="company" tabIndex={-1} autoComplete="off" aria-hidden="true"
    value={value} onChange={(e) => onChange(e.target.value)}
    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
    data-testid={`${testId}-hp`}
  />
);

/* Echtes Live-Anmeldeformular für den DIMEDIAL-Newsletter (öffentlich, Double-Opt-In). */
export const LiveNewsletter = ({ testId = "live-newsletter" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [hp, setHp] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [state, setState] = useState("idle"); // idle | loading | pending | already
  const [err, setErr] = useState("");
  const { chal, ans, setAns, reload } = useChallenge();

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    if (!privacy) { setErr("Bitte akzeptiere die Datenschutzbestimmungen."); return; }
    setErr(""); setState("loading");
    try { setState(await subscribe({ email, name, hp, cc: chal?.token || "", ca: ans })); }
    catch (e2) {
      setErr(e2.message); setState("idle");
      if (e2.code === "challenge" || e2.code === "challenge_wrong") reload();
    }
  };

  const done = state === "pending" || state === "already";
  return (
    <div data-testid={testId} className="w-full max-w-md rounded-3xl bg-white shadow-[0_28px_70px_-28px_rgba(0,0,0,0.55)] border border-[#111]/10 overflow-hidden">
      <div className="bg-[#111] px-6 py-4 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-white font-black tracking-wide text-sm">
          <span className="h-6 w-6 rounded-full grid place-items-center text-[#111] font-black" style={{ background: NEON }}>R</span> DIMEDIAL
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FDD105] text-[#111] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide" data-testid={`${testId}-livebadge`}>
          <span className="h-1.5 w-1.5 rounded-full bg-[#111] animate-pulse" /> Live-Demo
        </span>
      </div>
      <div className="px-6 py-6">
        {done ? (
          <div className="text-center py-3" data-testid={`${testId}-success`} data-status={state}>
            <div className="mx-auto h-12 w-12 rounded-full grid place-items-center" style={{ background: NEON }}><Check size={24} strokeWidth={3} className="text-[#111]" /></div>
            <h3 className="mt-4 text-lg font-black text-[#111]">{state === "already" ? "Du bist schon dabei 🎉" : "Fast geschafft!"}</h3>
            <p className="mt-1.5 text-sm text-[#555] leading-relaxed">
              {state === "already"
                ? "Diese E-Mail-Adresse ist bereits für den Newsletter angemeldet."
                : "Wir haben dir eine E-Mail geschickt. Bitte klick den Bestätigungslink darin – danach bist du dabei."}
            </p>
            <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#888]"><ShieldCheck size={13} className="text-[#1F5A3E]" /> Double-Opt-In · DSGVO-konform</p>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-black text-[#111] leading-tight">Newsletter abonnieren</h3>
            <p className="mt-1.5 text-sm text-[#555] leading-relaxed">Trag dich ein und erlebe selbst, wie die DIMEDIAL-Newsletter-Anmeldung funktioniert – inklusive echter Bestätigungsmail.</p>
            <form onSubmit={submit} className="mt-4 space-y-2.5">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dein Name (optional)" autoComplete="name"
                className="w-full rounded-xl border border-[#E4E1D8] bg-[#FBFAF5] px-4 py-3 text-sm text-[#111] outline-none transition-colors focus:border-[#111]" data-testid={`${testId}-name`} />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="deine@email.de" autoComplete="email"
                className="w-full rounded-xl border border-[#E4E1D8] bg-[#FBFAF5] px-4 py-3 text-sm text-[#111] outline-none transition-colors focus:border-[#111]" data-testid={`${testId}-email`} />
              <Honeypot value={hp} onChange={setHp} testId={testId} />
              {chal && (
                <label className="flex items-center gap-3 rounded-xl border border-[#E4E1D8] bg-[#FBFAF5] px-4 py-2.5 text-sm text-[#555]">
                  <span className="flex-1">Kurze Frage: Wie viel ist <strong className="text-[#111]">{chal.a} + {chal.b}</strong>?</span>
                  <input value={ans} onChange={(e) => setAns(e.target.value)} inputMode="numeric" required aria-label="Rechenaufgabe"
                    className="w-14 rounded-lg border border-[#E4E1D8] bg-white px-2 py-1.5 text-sm text-center text-[#111] outline-none focus:border-[#111]" data-testid={`${testId}-captcha`} />
                </label>
              )}
              <label className="flex items-start gap-2 text-[11px] text-[#666] leading-relaxed cursor-pointer">
                <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#111] shrink-0" data-testid={`${testId}-privacy-check`} />
                <span>Ich habe die <Link to="/datenschutz" target="_blank" className="underline hover:text-[#111]" data-testid={`${testId}-privacy`}>Datenschutzbestimmungen</Link> gelesen und akzeptiere sie.</span>
              </label>
              {err && <p className="text-xs text-[#B42318] border-l-2 border-[#B42318]/50 pl-2.5" data-testid={`${testId}-error`}>{err}</p>}
              <button type="submit" disabled={state === "loading"} data-testid={`${testId}-submit`}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#111] text-white px-5 py-3 text-sm font-bold transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60">
                {state === "loading" ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Jetzt anmelden
              </button>
            </form>
            <p className="mt-3 flex items-start gap-1.5 text-[11px] text-[#888] leading-relaxed">
              <ShieldCheck size={13} className="mt-0.5 shrink-0 text-[#1F5A3E]" />
              <span>Double-Opt-In: Du erhältst eine Bestätigungsmail. Abmeldung jederzeit mit einem Klick.</span>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

/* Dunkler Vollbreit-Abschnitt mit Text links + Live-Formular rechts (Startseite & Preise). */
export const LiveNewsletterSection = ({ testId = "live-newsletter-section" }) => (
  <section className="py-16 md:py-24 bg-[#111] text-white overflow-hidden" data-testid={testId}>
    <div className="mx-auto max-w-6xl px-5 md:px-8 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
        <span className="inline-flex items-center gap-2 rounded-full bg-[#FDD105] text-[#111] px-3 py-1 text-xs font-bold uppercase tracking-wide"><Mail size={14} strokeWidth={2.4} /> Echter Live-Newsletter</span>
        <h2 className="mt-5 text-3xl md:text-5xl font-black leading-[1.03] uppercase">Teste es selbst – <span className="bg-[#FDD105] text-[#111] px-2">direkt hier.</span></h2>
        <p className="mt-5 text-white/75 leading-relaxed max-w-md">So einfach melden sich deine Kund:innen später bei dir an. Trag dich ein, bestätige die E-Mail (Double-Opt-In) und sieh, wie sauber und schön das läuft. Genau dieses System steckt in jedem DIMEDIAL-Paket – <strong className="text-white">der Kundennewsletter ist kostenlos dabei.</strong></p>
        <ul className="mt-6 space-y-2.5 text-sm text-white/80">
          {["Im eigenen Design & Logo", "DSGVO-konform mit Double-Opt-In", "Per Link oder iFrame in jede Website"].map((t) => (
            <li key={t} className="flex items-center gap-2.5"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[#111]" style={{ background: NEON }}><Check size={13} strokeWidth={3} /></span> {t}</li>
          ))}
        </ul>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="flex md:justify-end">
        <LiveNewsletter testId={`${testId}-form`} />
      </motion.div>
    </div>
  </section>
);

/* Kompaktes Inline-Formular für den (dunklen) Footer. */
export const LiveNewsletterCompact = ({ testId = "footer-newsletter" }) => {
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const { chal, ans, setAns, reload } = useChallenge();

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    if (!privacy) { setErr("Bitte akzeptiere die Datenschutzbestimmungen."); return; }
    setErr(""); setState("loading");
    try { setState(await subscribe({ email, name: "", hp, cc: chal?.token || "", ca: ans })); }
    catch (e2) {
      setErr(e2.message); setState("idle");
      if (e2.code === "challenge" || e2.code === "challenge_wrong") reload();
    }
  };

  const done = state === "pending" || state === "already";
  return (
    <div data-testid={testId} className="w-full max-w-md">
      <p className="inline-flex items-center gap-2 text-sm font-bold text-paper"><Mail size={15} strokeWidth={2} className="text-gold" /> DIMEDIAL Newsletter</p>
      {done ? (
        <p className="mt-2.5 inline-flex items-start gap-2 text-sm text-grey leading-relaxed" data-testid={`${testId}-success`} data-status={state}>
          <Check size={15} className="mt-0.5 shrink-0 text-gold" />
          {state === "already" ? "Du bist bereits angemeldet." : "Schau in dein Postfach und bestätige den Link (Double-Opt-In)."}
        </p>
      ) : (
        <>
          <form onSubmit={submit} className="mt-2.5 space-y-2.5">
            <div className="flex flex-col sm:flex-row gap-2.5">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="deine@email.de" autoComplete="email"
                className="flex-1 min-w-0 rounded-full border border-white/20 bg-white/5 px-4 py-2.5 text-sm text-paper placeholder:text-grey/60 outline-none transition-colors focus:border-gold" data-testid={`${testId}-email`} />
              <button type="submit" disabled={state === "loading"} data-testid={`${testId}-submit`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FDD105] text-[#111] px-5 py-2.5 text-sm font-bold transition-transform duration-300 hover:scale-[1.03] active:scale-95 disabled:opacity-60 shrink-0">
                {state === "loading" ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />} Anmelden
              </button>
            </div>
            <Honeypot value={hp} onChange={setHp} testId={testId} />
            {chal && (
              <label className="flex items-center gap-2.5 text-xs text-grey">
                <span>Kurze Frage: <strong className="text-paper">{chal.a} + {chal.b}</strong> =</span>
                <input value={ans} onChange={(e) => setAns(e.target.value)} inputMode="numeric" required aria-label="Rechenaufgabe"
                  className="w-12 rounded-md border border-white/20 bg-white/5 px-2 py-1 text-sm text-center text-paper outline-none focus:border-gold" data-testid={`${testId}-captcha`} />
              </label>
            )}
            <label className="flex items-start gap-2 text-[11px] text-grey/80 leading-relaxed cursor-pointer">
              <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-gold shrink-0" data-testid={`${testId}-privacy-check`} />
              <span>Ich akzeptiere die <Link to="/datenschutz" className="underline hover:text-paper" data-testid={`${testId}-privacy`}>Datenschutzbestimmungen</Link>.</span>
            </label>
          </form>
          {err && <p className="mt-2 text-xs text-[#F7B4AD]" data-testid={`${testId}-error`}>{err}</p>}
          <p className="mt-2 text-[11px] text-grey/70">Kostenlos · Double-Opt-In · jederzeit abmeldbar.</p>
        </>
      )}
    </div>
  );
};

export default LiveNewsletter;
